import { createContext, useMemo, useContext } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeContextProvider');
  }
  return context;
};

export const ThemeContextProvider = ({ children }) => {
  // Dark mode only
  const mode = 'dark';

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'dark',
          primary: {
            main: '#90caf9',
            light: '#e3f2fd',
            dark: '#42a5f5',
          },
          secondary: {
            main: '#f48fb1',
            light: '#ffc1e3',
            dark: '#bf5f82',
          },
          background: {
            default: '#212529',
            paper: '#2c3034',
          },
          text: {
            primary: '#DEE2E6',
            secondary: '#ADB5BD',
          },
          success: {
            main: '#66bb6a',
          },
          warning: {
            main: '#ffa726',
          },
          error: {
            main: '#f44336',
          },
          info: {
            main: '#29b6f6',
          },
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            color: '#FFFFFF',
          },
          h2: {
            color: '#FFFFFF',
          },
          h3: {
            color: '#FFFFFF',
          },
          h4: {
            color: '#FFFFFF',
          },
          h5: {
            color: '#FFFFFF',
          },
          h6: {
            color: '#FFFFFF',
          },
          body1: {
            color: '#DEE2E6',
          },
          body2: {
            color: '#ADB5BD',
          },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                overflowX: 'hidden',
              },
            },
          },
          MuiContainer: {
            styleOverrides: {
              root: {
                '@media (max-width: 600px)': {
                  paddingLeft: '10px',
                  paddingRight: '10px',
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundColor: '#2c3034',
                backgroundImage: 'none',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: '#2c3034',
                backgroundImage: 'none',
              },
            },
          },
        },
      }),
    []
  );

  return (
    <ThemeContext.Provider value={{ mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
